import React, { useState } from 'react';
import {
    Card,
    Button,
    Space,
    Tag,
    Typography,
    Descriptions,
    Alert,
    Modal,
    Spin,
    message,
    Input
} from 'antd';
import {
    FileTextOutlined,
    BarcodeOutlined,
    SearchOutlined,
    EnvironmentOutlined,
    ClockCircleOutlined,
    DeleteOutlined
} from '@ant-design/icons';
import { useTranslation } from '../../hooks/useTranslation';
import { useDeleteJdExpressOrder } from '../../hooks/useGraphQL';
import { JdExpressOrder, JdExpressRouteInfo } from '../../types/jd-express.types';
import { usePermissions } from '../../hooks/usePermissions';

const { Text } = Typography;
const { TextArea } = Input;



// Helper function to get localized View PDF button text
const getViewPdfButtonText = (locale: string): string => {
    switch (locale) {
        case 'zh-CN':
            return '查看京东PDF';
        case 'zh-HK':
            return '查看京東PDF';
        default:
            return 'View JD Express PDF';
    }
};

// Helper function to get localized Cloud Print button text
const getCloudPrintButtonText = (locale: string): string => {
    switch (locale) {
        case 'zh-CN':
            return '打印运单';
        case 'zh-HK':
            return '打印運單';
        default:
            return 'Print Waybill';
    }
};

// Helper function to get waybill status color
const getWaybillStatusColor = (status: string): string => {
    switch (status) {
        case 'created':
        case 'success':
            return 'green';
        case 'failed':
        case 'error':
            return 'red';
        case 'pending':
        case 'processing':
            return 'orange';
        case 'cancelled':
            return 'gray';
        case 'delivered':
            return 'blue';
        case 'returned':
            return 'purple';
        default:
            return 'default';
    }
};

// Helper function to get localized waybill status text
const getLocalizedWaybillStatus = (status: string, t: (k: string) => string): string => {
    switch (status) {
        case 'created':
        case 'success':
            return t('appointments.jdExpress.statuses.created');
        case 'failed':
        case 'error':
            return t('appointments.jdExpress.statuses.failed');
        case 'pending':
        case 'processing':
            return t('appointments.jdExpress.statuses.processing');
        case 'cancelled':
            return t('appointments.jdExpress.statuses.cancelled');
        case 'delivered':
            return t('appointments.jdExpress.statuses.delivered');
        case 'returned':
            return t('appointments.jdExpress.statuses.returned');
        default:
            return status;
    }
};

// Helper function to format date with enhanced locale support
const formatDate = (dateString: string, locale: string): string => {
    try {
        const date = new Date(dateString);
        
        // Enhanced locale mapping for better date formatting
        const localeMap: { [key: string]: string } = {
            'en': 'en-US',
            'zh-CN': 'zh-CN',
            'zh-HK': 'zh-HK'
        };
        
        const mappedLocale = localeMap[locale] || 'en-US';
        
        return date.toLocaleDateString(mappedLocale, {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        });
    } catch {
        return dateString;
    }
};

// Helper function to get localized route status color
const getRouteStatusColor = (status: string): string => {
    // Handle Chinese status messages from JD Express
    if (status?.includes('已收取快件') || status?.includes('已揽收')) {
        return 'blue';
    }
    if (status?.includes('已签收') || status?.includes('已送达')) {
        return 'green';
    }
    if (status?.includes('运输中') || status?.includes('派送中') || status?.includes('在途')) {
        return 'blue';
    }
    if (status?.includes('已发货') || status?.includes('已发出')) {
        return 'blue';
    }
    if (status?.includes('待处理') || status?.includes('等待中') || status?.includes('待揽收')) {
        return 'orange';
    }
    if (status?.includes('异常') || status?.includes('失败') || status?.includes('退回')) {
        return 'red';
    }
    if (status?.includes('已取消')) {
        return 'gray';
    }
    
    // Handle English status messages
    switch (status?.toLowerCase()) {
        case 'delivered':
        case 'completed':
            return 'green';
        case 'in_transit':
        case 'shipping':
        case 'on_the_way':
            return 'blue';
        case 'pending':
        case 'processing':
        case 'picked_up':
            return 'orange';
        case 'failed':
        case 'error':
        case 'returned':
            return 'red';
        case 'cancelled':
            return 'gray';
        default:
            return 'default';
    }
};

// Helper function to get localized route status text
const getLocalizedRouteStatus = (status: string, t: (k: string) => string): string => {
    // Handle Chinese status messages from JD Express
    if (status?.includes('已收取快件') || status?.includes('已揽收')) {
        return t('appointments.jdExpress.routeStatus.pickedUp') || 'Package Picked Up';
    }
    if (status?.includes('已签收') || status?.includes('已送达')) {
        return t('appointments.jdExpress.routeStatus.delivered') || 'Delivered';
    }
    if (status?.includes('运输中') || status?.includes('派送中') || status?.includes('在途')) {
        return t('appointments.jdExpress.routeStatus.inTransit') || 'In Transit';
    }
    if (status?.includes('已发货') || status?.includes('已发出')) {
        return t('appointments.jdExpress.routeStatus.shipped') || 'Shipped';
    }
    if (status?.includes('待处理') || status?.includes('等待中') || status?.includes('待揽收')) {
        return t('appointments.jdExpress.routeStatus.pending') || 'Pending';
    }
    if (status?.includes('异常') || status?.includes('失败')) {
        return t('appointments.jdExpress.routeStatus.failed') || 'Failed';
    }
    if (status?.includes('退回')) {
        return t('appointments.jdExpress.routeStatus.returned') || 'Returned';
    }
    if (status?.includes('已取消')) {
        return t('appointments.jdExpress.routeStatus.cancelled') || 'Cancelled';
    }
    
    // Handle English status messages
    switch (status?.toLowerCase()) {
        case 'delivered':
            return t('appointments.jdExpress.routeStatus.delivered') || 'Delivered';
        case 'in_transit':
        case 'on_the_way':
            return t('appointments.jdExpress.routeStatus.inTransit') || 'In Transit';
        case 'shipping':
            return t('appointments.jdExpress.routeStatus.shipping') || 'Shipping';
        case 'pending':
            return t('appointments.jdExpress.routeStatus.pending') || 'Pending';
        case 'processing':
            return t('appointments.jdExpress.routeStatus.processing') || 'Processing';
        case 'picked_up':
            return t('appointments.jdExpress.routeStatus.pickedUp') || 'Picked Up';
        case 'failed':
            return t('appointments.jdExpress.routeStatus.failed') || 'Failed';
        case 'cancelled':
            return t('appointments.jdExpress.routeStatus.cancelled') || 'Cancelled';
        case 'returned':
            return t('appointments.jdExpress.routeStatus.returned') || 'Returned';
        default:
            return status || t('appointments.jdExpress.routeStatus.unknown') || 'Unknown';
    }
};

interface JdExpressCardProps {
    appointmentId?: string;
    onCreateJdExpressOrder: () => Promise<void>;
    creatingJdOrder: boolean;
    onCloudPrintWaybill?: (waybillNo: string) => Promise<void>;
    onViewJdExpressPdf?: (waybillNo: string) => void;
    onDeleteJdExpressOrder?: () => void;
    showJdExpressButton?: boolean;
    showRouteLookup?: boolean;
    style?: React.CSSProperties;
    locale?: string;
    jdExpressOrders?: JdExpressOrder[];
    title?: string;
    showTitle?: boolean;
}

const JdExpressCard: React.FC<JdExpressCardProps> = ({
    appointmentId,
    onCreateJdExpressOrder,
    creatingJdOrder,
    onCloudPrintWaybill,
    onViewJdExpressPdf,
    onDeleteJdExpressOrder,
    showJdExpressButton = true,
    showRouteLookup = true,
    style,
    locale,
    jdExpressOrders = [],
    title,
    showTitle = true
}) => {
    const { t, currentLanguage } = useTranslation();
    const { canDeleteAppointments } = usePermissions();
    const [routeModalVisible, setRouteModalVisible] = useState(false);
    const [routeInfo, setRouteInfo] = useState<JdExpressRouteInfo | null>(null);
    const [routeLoading, setRouteLoading] = useState(false);
    const [deleteModalVisible, setDeleteModalVisible] = useState(false);
    const [deleteReason, setDeleteReason] = useState('');

    // JD Express delete hook
    const [deleteJdExpressOrder, { loading: deleteLoading }] = useDeleteJdExpressOrder();

    // Use provided locale or fall back to current language
    const activeLocale = locale || currentLanguage;
    
    // Enhanced locale detection and validation
    const isValidLocale = (loc: string): boolean => {
        return ['en', 'zh-CN', 'zh-HK'].includes(loc);
    };
    
    const validatedLocale = isValidLocale(activeLocale) ? activeLocale : 'en';

    // Check if there are any JD Express orders with waybill numbers
    const hasWaybill = jdExpressOrders.some(order => order.waybill_no);
    const latestOrder = jdExpressOrders.length > 0 ? jdExpressOrders[0] : null;


    const handleViewJdExpressPdf = () => {
        if (latestOrder?.waybill_no && onViewJdExpressPdf) {
            onViewJdExpressPdf(latestOrder.waybill_no);
        }
    };

    const handleCloudPrintWaybill = async () => {
        if (latestOrder?.waybill_no && onCloudPrintWaybill) {
            try {
                await onCloudPrintWaybill(latestOrder.waybill_no);
            } catch (error) {
                console.error('Error cloud printing JD Express waybill:', error);
            }
        }
    };

    // Route lookup handlers - TODO: Implement JD Express route search
    const handleRouteLookup = async () => {
        if (!appointmentId) {
            message.error(t('appointments.jdExpress.noAppointmentId'));
            return;
        }

        setRouteLoading(true);
        setRouteModalVisible(true);

        try {
            // TODO: Implement JD Express route search API call
            // For now, show a placeholder message
            message.info(t('appointments.jdExpress.routeLookupNotImplemented') || 'Route lookup not yet implemented for JD Express');
            setRouteInfo(null);
        } catch (error) {
            console.error('Route lookup error:', error);
            message.error(t('appointments.jdExpress.routeLookupError'));
            setRouteInfo(null);
        } finally {
            setRouteLoading(false);
        }
    };

    const handleCloseRouteModal = () => {
        setRouteModalVisible(false);
        setRouteInfo(null);
    };

    // Delete handlers
    const handleDeleteJdExpressOrder = () => {
        setDeleteModalVisible(true);
    };

    const handleConfirmDelete = async () => {
        if (!latestOrder?.request_id) {
            message.error(t('appointments.jdExpress.noOrderToDelete'));
            return;
        }

        try {
            await deleteJdExpressOrder({
                variables: {
                    input: {
                        orderId: latestOrder.request_id,
                        reason: deleteReason || undefined
                    }
                }
            });

            message.success(t('appointments.jdExpress.orderDeletedSuccessfully'));
            setDeleteModalVisible(false);
            setDeleteReason('');
            
            // Call the parent callback to refresh data
            if (onDeleteJdExpressOrder) {
                onDeleteJdExpressOrder();
            }
        } catch (error) {
            console.error('Error deleting JD Express order:', error);
            message.error(t('appointments.jdExpress.deleteOrderFailed'));
        }
    };

    const handleCancelDelete = () => {
        setDeleteModalVisible(false);
        setDeleteReason('');
    };

    // Don't render if no JD Express waybill exists
    if (!hasWaybill) {
        return null;
    }

    return (
        <>
            <Card
                title={showTitle ? (title || t('appointments.jdExpress.title')) : undefined}
                style={{ 
                    marginBottom: 0, 
                    ...style 
                }}
                extra={
                    <Space>
                        {/* {hasWaybill && showRouteLookup && (
                            <Button
                                type="default"
                                icon={<SearchOutlined />}
                                size="small"
                                onClick={handleRouteLookup}
                                loading={routeLoading}
                                title={t('appointments.jdExpress.lookupRoute')}
                            >
                                {t('appointments.jdExpress.lookupRoute')}
                            </Button>
                        )} */}
                        {hasWaybill && canDeleteAppointments() && (
                            <Button
                                type="default"
                                danger
                                icon={<DeleteOutlined />}
                                size="small"
                                onClick={handleDeleteJdExpressOrder}
                                loading={deleteLoading}
                                title={t('appointments.jdExpress.deleteOrder')}
                            >
                                {t('appointments.jdExpress.deleteOrder')}
                            </Button>
                        )}
                        {hasWaybill && (
                            <>
                                <Button
                                    type="default"
                                    icon={<FileTextOutlined />}
                                    size="small"
                                    onClick={handleCloudPrintWaybill}
                                    title={getCloudPrintButtonText(validatedLocale)}
                                >
                                    {getCloudPrintButtonText(validatedLocale)}
                                </Button>
                                <Button
                                    type="primary"
                                    icon={<FileTextOutlined />}
                                    size="small"
                                    onClick={handleViewJdExpressPdf}
                                    title={getViewPdfButtonText(validatedLocale)}
                                >
                                    {getViewPdfButtonText(validatedLocale)}
                                </Button>
                            </>
                        )}
                    </Space>
                }
            >
                {/* JD Express Waybill Information */}
                {hasWaybill && latestOrder && (
                    <div>
                        <div style={{
                            border: '1px solid #d9d9d9',
                            borderRadius: '6px',
                            padding: '16px',
                            backgroundColor: '#fafafa'
                        }}>
                            <div style={{ marginBottom: 12 }}>
                                <Space>
                                    <BarcodeOutlined style={{ color: '#1890ff' }} />
                                    <Text strong style={{ fontSize: '14px' }}>
                                        {t('appointments.jdExpress.waybillInfo')}
                                    </Text>
                                </Space>
                            </div>

                            <Descriptions column={1} size="small" bordered>
                                {latestOrder.waybill_no && (
                                    <Descriptions.Item
                                        label={
                                            <Space>
                                                <BarcodeOutlined />
                                                {t('appointments.jdExpress.waybillNo')}
                                            </Space>
                                        }
                                    >
                                        <Text code style={{ fontSize: '13px' }}>
                                            {latestOrder.waybill_no}
                                        </Text>
                                    </Descriptions.Item>
                                )}

                                {latestOrder.order_id && (
                                    <Descriptions.Item
                                        label={
                                            <Space>
                                                <BarcodeOutlined />
                                                {t('appointments.jdExpress.orderId')}
                                            </Space>
                                        }
                                    >
                                        <Text code style={{ fontSize: '13px' }}>
                                            {latestOrder.order_id}
                                        </Text>
                                    </Descriptions.Item>
                                )}

                                {latestOrder.tracking_number && (
                                    <Descriptions.Item
                                        label={
                                            <Space>
                                                <BarcodeOutlined />
                                                {t('appointments.jdExpress.trackingNumber')}
                                            </Space>
                                        }
                                    >
                                        <Text code style={{ fontSize: '13px' }}>
                                            {latestOrder.tracking_number}
                                        </Text>
                                    </Descriptions.Item>
                                )}

                                {latestOrder.status && (
                                    <Descriptions.Item
                                        label={
                                            <Space>
                                                <BarcodeOutlined />
                                                {t('appointments.jdExpress.status')}
                                            </Space>
                                        }
                                    >
                                        <Tag color={getWaybillStatusColor(latestOrder.status)}>
                                            {getLocalizedWaybillStatus(latestOrder.status, t)}
                                        </Tag>
                                    </Descriptions.Item>
                                )}

                                {latestOrder.created_at && (
                                    <Descriptions.Item
                                        label={
                                            <Space>
                                                <ClockCircleOutlined />
                                                {t('appointments.jdExpress.createdAt')}
                                            </Space>
                                        }
                                    >
                                        <Text>{formatDate(latestOrder.created_at, validatedLocale)}</Text>
                                    </Descriptions.Item>
                                )}

                                {/* Display metadata information if available */}
                                {latestOrder.metadata && (
                                    <>
                                        {latestOrder.metadata.deliveryPromiseTime && (
                                            <Descriptions.Item
                                                label={
                                                    <Space>
                                                        <ClockCircleOutlined />
                                                        {t('appointments.jdExpress.estimatedDelivery')}
                                                    </Space>
                                                }
                                            >
                                                <Text>
                                                    {formatDate(new Date(latestOrder.metadata.deliveryPromiseTime).toISOString(), validatedLocale)}
                                                </Text>
                                            </Descriptions.Item>
                                        )}

                                        {latestOrder.metadata.totalAmount && (
                                            <Descriptions.Item
                                                label={
                                                    <Space>
                                                        <Text>¥</Text>
                                                        {t('appointments.jdExpress.totalAmount')}
                                                    </Space>
                                                }
                                            >
                                                <Text strong>
                                                    ¥{latestOrder.metadata.totalAmount}
                                                    {latestOrder.metadata.currency && ` ${latestOrder.metadata.currency}`}
                                                </Text>
                                            </Descriptions.Item>
                                        )}

                                        {latestOrder.metadata.startSiteName && (
                                            <Descriptions.Item
                                                label={
                                                    <Space>
                                                        <EnvironmentOutlined />
                                                        {t('appointments.jdExpress.startSite')}
                                                    </Space>
                                                }
                                            >
                                                <Text>{latestOrder.metadata.startSiteName}</Text>
                                            </Descriptions.Item>
                                        )}

                                        {latestOrder.metadata.endSiteName && (
                                            <Descriptions.Item
                                                label={
                                                    <Space>
                                                        <EnvironmentOutlined />
                                                        {t('appointments.jdExpress.endSite')}
                                                    </Space>
                                                }
                                            >
                                                <Text>{latestOrder.metadata.endSiteName}</Text>
                                            </Descriptions.Item>
                                        )}
                                    </>
                                )}
                            </Descriptions>

                            {latestOrder.error_message && (
                                <div style={{ marginTop: 12 }}>
                                    <Alert
                                        message={t('appointments.jdExpress.error')}
                                        description={latestOrder.error_message}
                                        type="error"
                                        showIcon
                                    />
                                </div>
                            )}
                        </div>
                    </div>
                )}

            </Card>

            {/* Route Lookup Modal */}
            <Modal
                title={
                    <Space>
                        <SearchOutlined />
                        {t('appointments.jdExpress.routeLookupTitle')}
                    </Space>
                }
                open={routeModalVisible}
                onCancel={handleCloseRouteModal}
                footer={[
                    <Button key="close" onClick={handleCloseRouteModal}>
                        {t('common.close')}
                    </Button>
                ]}
                width={800}
                destroyOnClose
            >
                {routeLoading ? (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Spin size="large" />
                        <div style={{ marginTop: 16 }}>
                            {t('appointments.jdExpress.loadingRouteInfo')}
                        </div>
                    </div>
                ) : routeInfo ? (
                    <div>
                        <Descriptions column={1} bordered size="small">
                            {routeInfo.waybillNo && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <BarcodeOutlined />
                                            {t('appointments.jdExpress.waybillNo')}
                                        </Space>
                                    }
                                >
                                    <Text code style={{ fontSize: '13px' }}>
                                        {routeInfo.waybillNo}
                                    </Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.trackingNumber && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <BarcodeOutlined />
                                            {t('appointments.jdExpress.trackingNumber')}
                                        </Space>
                                    }
                                >
                                    <Text code style={{ fontSize: '13px' }}>
                                        {routeInfo.trackingNumber}
                                    </Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.currentStatus && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <Tag color={getRouteStatusColor(routeInfo.currentStatus || '')} />
                                            {t('appointments.jdExpress.currentStatus')}
                                        </Space>
                                    }
                                >
                                    <Tag color={getRouteStatusColor(routeInfo.currentStatus || '')}>
                                        {getLocalizedRouteStatus(routeInfo.currentStatus || '', t)}
                                    </Tag>
                                </Descriptions.Item>
                            )}

                            {routeInfo.currentLocation && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <EnvironmentOutlined />
                                            {t('appointments.jdExpress.currentLocation')}
                                        </Space>
                                    }
                                >
                                    <Text>{routeInfo.currentLocation}</Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.estimatedDelivery && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <ClockCircleOutlined />
                                            {t('appointments.jdExpress.estimatedDelivery')}
                                        </Space>
                                    }
                                >
                                    <Text>{routeInfo.estimatedDelivery ? formatDate(routeInfo.estimatedDelivery, validatedLocale) : ''}</Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.lastUpdateTime && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <ClockCircleOutlined />
                                            {t('appointments.jdExpress.lastUpdateTime')}
                                        </Space>
                                    }
                                >
                                    <Text>{routeInfo.lastUpdateTime ? formatDate(routeInfo.lastUpdateTime, validatedLocale) : ''}</Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.routeInfo && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <SearchOutlined />
                                            {t('appointments.jdExpress.routeInfo')}
                                        </Space>
                                    }
                                >
                                    <div>
                                        {routeInfo.routeInfo.split('; ').map((routeEntry, index) => (
                                            <div key={index} style={{ 
                                                marginBottom: index < routeInfo.routeInfo!.split('; ').length - 1 ? 8 : 0,
                                                padding: '8px',
                                                backgroundColor: index === 0 ? '#f6ffed' : '#fafafa',
                                                border: index === 0 ? '1px solid #b7eb8f' : '1px solid #d9d9d9',
                                                borderRadius: '4px'
                                            }}>
                                                <Text style={{ 
                                                    fontSize: '13px',
                                                    color: index === 0 ? '#52c41a' : '#666'
                                                }}>
                                                    {routeEntry}
                                                </Text>
                                                {index === 0 && (
                                                    <Tag color="green" style={{ marginLeft: 8 }}>
                                                        {t('appointments.jdExpress.latestUpdate')}
                                                    </Tag>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </Descriptions.Item>
                            )}

                            {routeInfo.deliveryAddress && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <EnvironmentOutlined />
                                            {t('appointments.jdExpress.deliveryAddress')}
                                        </Space>
                                    }
                                >
                                    <Text>{routeInfo.deliveryAddress}</Text>
                                </Descriptions.Item>
                            )}

                            {routeInfo.senderAddress && (
                                <Descriptions.Item
                                    label={
                                        <Space>
                                            <EnvironmentOutlined />
                                            {t('appointments.jdExpress.senderAddress')}
                                        </Space>
                                    }
                                >
                                    <Text>{routeInfo.senderAddress}</Text>
                                </Descriptions.Item>
                            )}
                        </Descriptions>
                    </div>
                ) : (
                    <div style={{ textAlign: 'center', padding: '40px' }}>
                        <Alert
                            message={t('appointments.jdExpress.noRouteInfo')}
                            description={t('appointments.jdExpress.noRouteInfoDesc')}
                            type="info"
                            showIcon
                        />
                    </div>
                )}
            </Modal>

            {/* Delete Confirmation Modal */}
            <Modal
                title={
                    <Space>
                        <DeleteOutlined />
                        {t('appointments.jdExpress.deleteOrderTitle')}
                    </Space>
                }
                open={deleteModalVisible}
                onOk={handleConfirmDelete}
                onCancel={handleCancelDelete}
                confirmLoading={deleteLoading}
                okText={t('common.delete')}
                cancelText={t('common.cancel')}
                okButtonProps={{ danger: true }}
                destroyOnClose
            >
                <div>
                    <p>{t('appointments.jdExpress.deleteOrderConfirm')}</p>
                    <p><strong>{t('appointments.jdExpress.waybillNo')}:</strong> {latestOrder?.waybill_no}</p>
                    {latestOrder?.order_id && (
                        <p><strong>{t('appointments.jdExpress.orderId')}:</strong> {latestOrder.order_id}</p>
                    )}
                    <div style={{ marginTop: 16 }}>
                        <label>{t('appointments.jdExpress.deleteReason')}:</label>
                        <TextArea
                            rows={3}
                            value={deleteReason}
                            onChange={(e) => setDeleteReason(e.target.value)}
                            placeholder={t('appointments.jdExpress.deleteReasonPlaceholder')}
                            style={{ marginTop: 8 }}
                        />
                    </div>
                </div>
            </Modal>
        </>
    );
};

export default JdExpressCard;
